import { motion } from "framer-motion"
import {MessageCircleDashed as MessageCircle, Clock, Mic, Brain, Smartphone, BookOpen} from 'lucide-react'

const features = [
  { icon: MessageCircle, text: "Conversação real pelo WhatsApp" },
  { icon: Clock, text: "Disponível 24h por dia, 7 dias por semana" },
  { icon: Mic, text: "Prática de pronúncia e fala desde o primeiro dia" },
  { icon: Brain, text: "Aprende seu ritmo e adapta as lições" },
  { icon: Smartphone, text: "Direto do seu celular, onde você estiver" },
  { icon: BookOpen, text: "+500 horas de conteúdo autoral" },
]

const ecosystem = [
  "Lives semanais com professores reais",
  "Comunidade ativa",
  "App próprio iOS e Android",
  "Método GO4D alinhado ao CEFR",
  "Certificação TOEFL iBT",
]

function LucyAISection() {
  return (
    <section className="relative bg-gradient-to-b from-gray-900 via-blue-950 to-indigo-950 py-16 sm:py-24 px-4 overflow-hidden">
      {/* Decorative orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" aria-hidden="true" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" aria-hidden="true" />
      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-5 bg-[linear-gradient(rgba(255,255,255,.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.1)_1px,transparent_1px)] bg-[size:40px_40px]" aria-hidden="true" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent text-sm font-bold tracking-widest uppercase mb-3">
            LUCY IA
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            A primeira tutora de inglês AI-First do Brasil
          </h2>
          <p className="text-blue-200 text-lg max-w-3xl mx-auto">
            integrada a um ecossistema completo de fluência.
          </p>
        </motion.div>

        {/* Main content: text + image */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
              Aprenda praticando conversação de verdade
            </h3>
            <p className="text-blue-200 mb-4">
              Com a Lucy, você aprende praticando conversação de verdade com sua professora particular, direto do seu celular pelo WhatsApp.
            </p>
            <p className="text-blue-300/80 text-sm mb-8 italic">
              E não estudando gramática isolada ou repetindo frases feitas como em cursos e métodos de escolas de inglês tradicionais.
            </p>

            <div className="space-y-3">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4 h-4 text-blue-400" />
                  </div>
                  <span className="text-white text-sm sm:text-base">{f.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src="https://static.lumi.new/85/8578737d31747385321aad267a0ff70a.webp"
              alt="Ecossistema EW Pass com Lucy IA"
              className="rounded-2xl shadow-2xl max-w-full w-full max-h-[500px] object-cover"
            />
          </motion.div>
        </div>

        {/* Ecosystem block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 sm:p-10"
        >
          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 text-center">
            Lucy é o cérebro. EW Pass é o corpo.
          </h3>
          <p className="text-blue-200 text-center mb-6">
            O ecossistema completo que potencializa a Lucy:
          </p>
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {ecosystem.map((item, i) => (
              <span
                key={i}
                className="bg-blue-500/20 text-blue-200 text-sm font-medium px-4 py-2 rounded-full border border-blue-400/20"
              >
                {item}
              </span>
            ))}
          </div>
          <p className="text-center text-blue-300 font-semibold">
            Tudo alimenta a inteligência da Lucy.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

export default LucyAISection
